import {
  ChipBadge,
  IStyles,
  IconWrapper,
  ImageWrapper,
  ScoreProgressBar,
  firstLetterImage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
} from '@geneo2-web/shared-ui';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  AssessmentEnum,
  ModeOfConductionEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { TeacherStudentResponseFetch } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { useState } from 'react';
import { getSubjectNameFromEnum } from '../../../Home/home.utils';

const styles: IStyles = {
  root: {
    // border: '1px solid red',
    mb: { xs: pxToRem(20), md: pxTovW(20) },
    p: {
      xs: `${pxToRem(20)} ${pxToRem(20)} 0 ${pxToRem(20)}`,
      md: `${pxTovW(0)}`,
    },
  },

  userDetailBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    mt: { xs: pxToRem(16), md: pxTovW(22) },
  },
  userImage: {
    width: { xs: pxToRem(60), md: pxTovW(87) },
    height: { xs: pxToRem(60), md: pxTovW(87) },
    borderRadius: '50%',
  },
  userNameBox: {
    flexGrow: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxTovW(15), lg: pxTovW(25) },
  },
  marksBox: {
    mt: { xs: pxToRem(21), md: pxTovW(32) },
    display: 'flex',
    boxShadow: `0px 0px ${pxTovW(10)} #0000001F`,
    borderRadius: pxTovW(15),
    p: {
      xs: `${pxToRem(20)} ${pxToRem(20)}`,
      md: `${pxTovW(25)} ${pxTovW(30)}`,
    },
  },
};
interface IProps {
  studentResponse?: TeacherStudentResponseFetch;
  totalMarks?: number;
  hwContent?: AssessmentContent | undefined;
}
export const HwPerformanceStatCard = (props: IProps) => {
  const { studentResponse, totalMarks, hwContent } = props;
  const [modalState, setModalState] = useState(false);

  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const percentageScore =
    ((studentResponse?.responseScore || 0) /
      (studentResponse?.maxScore || 100)) *
    100;

  const roundedPercentage = Math.round(percentageScore);
  const isOmrAssessment =
    hwContent?.assessment?.modeOfConduction ===
    ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_OMR;
  return (
    <Box sx={styles.root}>
      <Typography variant="h1">Performance</Typography>

      <Box sx={styles.userDetailBox}>
        {studentResponse && (
          <ImageWrapper
            name="student1"
            type="png"
            parentFolder="tempAssets"
            styles={styles.userImage}
            path={
              getMediaBasePath(studentResponse.profileImageUrl) ||
              firstLetterImage(studentResponse?.studentName)
            }
          />
        )}

        <Box sx={styles.userNameBox}>
          <Typography
            variant="h3"
            sx={{
              width: { xs: pxToRem(220), md: pxTovW(500), lg: pxTovW(400) },
              display: '-webkit-box',
              WebkitLineClamp: { xs: 1, md: 1 },
              wordWrap: 'break-word',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {studentResponse?.studentName || ''}
          </Typography>

          <Box sx={{ width: { xs: pxToRem(218), md: pxTovW(300) } }}>
            <ScoreProgressBar
              score={roundedPercentage}
              variant={largeScreen ? 'lg' : 'md'}
            />
          </Box>
        </Box>
      </Box>
      <Typography variant={largeScreen ? 'h2' : 'h1'} fontWeight="bold">
        Overall Summary
      </Typography>
      {hwContent?.assessment?.assessmentType ===
        AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT &&
      hwContent?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ? (
        <Box>
          {studentResponse?.multiSubjectMetrics?.map((section, index) => {
            // const subject = getSubjectNameFromEnum(section.subject);
            return (
              <Box
                key={index}
                sx={{
                  mt: { xs: pxToRem(21), md: pxTovW(32) },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: `0px 0px ${pxTovW(10)} #0000001F`,
                  borderRadius: pxTovW(15),
                  gap: { xs: pxToRem(10), md: pxTovW(10) },
                  p: {
                    xs: `${pxToRem(20)} ${pxToRem(20)}`,
                    md: `${pxTovW(25)} ${pxTovW(30)}`,
                  },
                }}
              >
                <ChipBadge
                  label={`${getSubjectNameFromEnum(section?.subject)} ${
                    section?.sectionTitle ?? ''
                  }`}
                  color="primary"
                  size="small"
                  sx={{
                    fontSize: pxToRem(10),
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: isOmrAssessment
                      ? 'space-evenly'
                      : 'space-between',
                  }}
                  key={index}
                >
                  <MarksComp
                    icon="clipboard-blue"
                    boldText={`${section.classSubjectScore || 0}`}
                    text={`${section.classSubjectMaxScore || 0}`}
                    label="Marks"
                  />
                  <MarksComp
                    icon="green-circle-check"
                    boldText={`${section.numberOfCorrectQuestions || 0}`}
                    text={`${section.numberOfQuestions || 0}`}
                    label="Correct"
                  />
                  {!isOmrAssessment && (
                    <MarksComp
                      icon="clock"
                      boldText={`${section.timeDurationInMin || 0}`}
                      label="Minutes"
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            ...styles.marksBox,
            justifyContent: isOmrAssessment ? 'space-evenly' : 'space-between',
          }}
        >
          <MarksComp
            icon="clipboard-blue"
            boldText={`${studentResponse?.metrics?.responseScore || 0}`}
            text={`${totalMarks || studentResponse?.metrics?.maxScore || 0}`}
            label="Marks"
          />
          <MarksComp
            icon="green-circle-check"
            boldText={`${studentResponse?.metrics?.correctCount || 0}`}
            text={`${studentResponse?.metrics?.totalQuestions || 0}`}
            label="Correct"
          />
          {!isOmrAssessment && (
            <MarksComp
              icon="clock"
              boldText={`${(
                (studentResponse?.metrics?.timeSpentInSec || 0) / 60
              ).toFixed(1)}`}
              label="Minutes"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

//^ Local component

const markStyles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  iconTextBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(8) },
  },

  textBox: {
    display: 'flex',
    alignItems: 'center',
  },
};

interface IMarksComp {
  icon: string;
  boldText: string;
  text?: string;
  label: string;
}
const MarksComp = ({ icon, boldText, text, label }: IMarksComp) => {
  return (
    <Box sx={markStyles.root}>
      <Box sx={markStyles.iconTextBox}>
        <IconWrapper name={icon} size="md" parentFolder="icons" type="png" />

        <Box sx={markStyles.textBox}>
          <Typography variant="h2" fontWeight="bold">
            {boldText}
          </Typography>
          {text && (
            <Typography variant="h3" color="text.disabled">
              /{text}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="h3" color="text.disabled">
        {label}
      </Typography>
    </Box>
  );
};
